import { useRef, useState } from 'react'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import LayersRoundedIcon from '@mui/icons-material/LayersRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import type { Goal } from '../types/timeline'
import { FloatingMenu } from './Common/FloatingMenu'
import { HiddenFileInput } from './Common'
import { Button } from './Common/Button'
import { Pressable } from './Common/Pressable'
import { WorkspaceItemPicker, type WorkspaceItemReference } from './Common/WorkspaceItemPicker'
import type { EditTarget, WorkspacePlanMode } from './AiGenerator/editTarget'
import type { DataContext } from '../types/aiChatSource'
import { useNls } from '../nls/nlsProvider'
import { getAiComposerCopy } from './AiMagicNls'

type Section = 'root' | 'timeline'

/**
 * Convert an EditTarget from selectedTargets into the node id the picker uses
 * for `selectedIds` lookups (the node's own id, not a targetKey string).
 * Upload/meeting/report/form/checklist_item targets don't map to picker nodes
 * except checklist_item which uses `itemId` — same id the picker emits.
 */
const editTargetToNodeId = (target: EditTarget): string => {
  switch (target.kind) {
    case 'goal': return target.goalId
    case 'task': return target.taskId
    case 'subtask': return target.subTaskId
    case 'checklist_item': return target.itemId
    default: return `__unmapped:${target.kind}`
  }
}

/**
 * Map a picker reference back into an EditTarget, using the ancestor chain
 * the picker attaches to every emitted reference. Ancestors carry the goal /
 * task / subtask ids the AI edit path expects.
 */
export const referenceToEditTarget = (
  reference: WorkspaceItemReference,
  goals: Goal[],
  planMode: WorkspacePlanMode = 'EDIT',
): EditTarget | null => {
  const goalId = reference.kind === 'goal'
    ? reference.id
    : reference.ancestors.find((ancestor) => ancestor.kind === 'goal')?.id
  const goal = goals.find((candidate) => candidate.id === goalId)
  if (!goal) return null
  switch (reference.kind) {
    case 'goal':
      return { kind: 'goal', goalId: goal.id, label: reference.label, planMode }
    case 'task': {
      const task = goal.tasks?.find((candidate) => candidate.id === reference.id)
      if (!task) return null
      return { kind: 'task', goalId: goal.id, taskId: task.id, label: reference.label, planMode }
    }
    case 'subtask': {
      let matchedTaskId = ''
      const subtask = goal.tasks?.flatMap((task) => (task.subTasks ?? []).map((candidate) => {
        if (candidate.id === reference.id) matchedTaskId = task.id
        return candidate
      })).find((candidate) => candidate.id === reference.id)
      if (!subtask || !matchedTaskId) return null
      return {
        kind: 'subtask',
        goalId: goal.id,
        taskId: matchedTaskId,
        subTaskId: subtask.id,
        label: reference.label,
        planMode,
      }
    }
    case 'checklist': {
      let matchedTaskId = ''
      let matchedSubtask: { id: string } | undefined
      for (const task of goal.tasks ?? []) {
        if (task.checklist?.some((item) => item.id === reference.id)) {
          matchedTaskId = task.id
          break
        }
        for (const subtask of task.subTasks ?? []) {
          if (subtask.checklist?.some((item) => item.id === reference.id)) {
            matchedTaskId = task.id
            matchedSubtask = subtask
            break
          }
        }
        if (matchedTaskId) break
      }
      const task = goal.tasks?.find((candidate) => candidate.id === matchedTaskId)
      if (!task) return null
      return {
        kind: 'checklist_item',
        goalId: goal.id,
        taskId: task.id,
        subTaskId: matchedSubtask?.id,
        itemId: reference.id,
        label: reference.label,
      }
    }
    default:
      return null
  }
}

export const AiComposerMenu = ({
  disabled,
  goals,
  selectedTargets,
  onToggleTarget,
  attachmentCount,
  attachmentLimit,
  onFiles,
  onOpenContextPicker,
  showPlanModeToggle = false,
  isPlanningMode = false,
  onPlanModeChange,
  triggerClassName,
  isMobile,
}: {
  disabled?: boolean
  goals: Goal[]
  selectedTargets: EditTarget[]
  onToggleTarget: (target: EditTarget) => void
  onAttach?: (context: DataContext) => void
  attachmentCount: number
  attachmentLimit: number
  onFiles: (files: File[]) => void
  onOpenContextPicker?: () => void
  canViewWorkspaceAssets?: boolean
  /** Render the Plan-mode toggle row inside the popover. CREATE-tab only. */
  showPlanModeToggle?: boolean
  /** Current plan-mode value. Ignored when showPlanModeToggle is false. */
  isPlanningMode?: boolean
  /** Plan-mode setter. Ignored when showPlanModeToggle is false. */
  onPlanModeChange?: (value: boolean) => void
  triggerClassName?: string
  isMobile?: boolean
}) => {
  const { t } = useNls()
  const copy = getAiComposerCopy(t)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [section, setSection] = useState<Section>('root')
  const hasAttachmentLimit = attachmentLimit !== -1
  const atLimit = hasAttachmentLimit && attachmentCount >= attachmentLimit
  const attachmentLimitLabel = hasAttachmentLimit ? String(attachmentLimit) : 'Unlimited'

  const close = () => {
    setOpen(false)
    setSection('root')
  }

  return (
    <div className="aiComposerMenuRoot" data-floating-menu-root="true">
      <Pressable
        ref={triggerRef}
        data-tour-id="ai-composer-add-context"
        className={triggerClassName ?? 'aiComposerIconButton'}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        aria-label={copy.addContext}
        title={copy.addContext}
        aria-expanded={open}
      ><AddRoundedIcon fontSize="small" /></Pressable>
      <HiddenFileInput
        ref={fileRef}
        multiple
        accept="image/*,audio/*,video/*,.pdf,.docx,.txt,.md,.xlsx,.xls,.csv"
        onFiles={(files) => {
          const list = Array.from(files)
          if (list.length) onFiles(list)
          close()
        }}
      />
      <FloatingMenu anchorRef={triggerRef} isOpen={open} onClose={close} placement="top-start" className={`aiComposerUnifiedMenu${isMobile ? ' aiComposerUnifiedMenu--mobile' : ''}`} role="menu" ariaLabel={copy.menuOptions}>
        {section === 'timeline' ? null : (
          <div className="aiComposerMenuHead">
            <span>{section === 'root' ? copy.contextCount(attachmentCount, attachmentLimitLabel) : ''}</span>
            {section !== 'root' && (
              <Button
                variant="tertiary"
                size="lg"
                icon={<ArrowBackRoundedIcon fontSize="inherit" />}
                onClick={() => setSection('root')}
              >
                {copy.back}
              </Button>
            )}
          </div>
        )}

        {section === 'root' && (
          <>
            <div className="aiComposerMenuGroupLabel">{copy.addToContext}</div>
            <Pressable
              className="aiComposerMenuItem"
              disabled={atLimit}
              onClick={() => {
                close()
                onOpenContextPicker?.()
              }}
            >
              <span className="aiComposerMenuIcon"><AutoAwesomeRoundedIcon fontSize="small" /></span>
              <div>
                <strong>{copy.attachContext}</strong>
                <small>{copy.attachContextHint}</small>
              </div>
            </Pressable>
            <Pressable className="aiComposerMenuItem" disabled={atLimit} onClick={() => setSection('timeline')}>
              <span className="aiComposerMenuIcon"><LayersRoundedIcon fontSize="small" /></span>
              <div><strong>{copy.selectTimelineItems}</strong><small>{copy.selectedCount(selectedTargets.length)}</small></div>
              <ChevronRightRoundedIcon fontSize="small" className="aiComposerMenuChevron" />
            </Pressable>

            {showPlanModeToggle && (
              <>
                <div className="aiComposerMenuDivider" />
                <div className="aiComposerMenuGroupLabel">{copy.operatingMode}</div>
                <label className="aiComposerMenuToggleRow" data-tour-id="ai-composer-plan-mode-toggle">
                  <span className="aiComposerMenuIcon"><AutoAwesomeRoundedIcon fontSize="small" /></span>
                  <span className="aiComposerMenuToggleCopy">
                    <strong>{copy.planMode}</strong>
                    <small>{copy.planModeHint}</small>
                  </span>
                  <span className={`aiComposerPillSwitch${isPlanningMode ? ' isOn' : ''}`} aria-hidden="true">
                    <span className="aiComposerPillSwitchThumb" />
                  </span>
                  {/* eslint-disable-next-line no-restricted-syntax -- Common Toggle lacks trailing track position and multi-line label slot for floating menu switch */}
                  <input
                    type="checkbox"
                    className="srOnlyCheckbox"
                    checked={isPlanningMode}
                    onChange={(event) => onPlanModeChange?.(event.target.checked)}
                  />
                </label>
              </>
            )}
          </>
        )}

        {section === 'timeline' && (
          <div className="aiComposerTimelinePicker">
            <WorkspaceItemPicker
              goals={goals}
              onSelect={(reference) => {
                const target = referenceToEditTarget(reference, goals)
                if (target) onToggleTarget(target)
              }}
              onClose={() => setSection('root')}
              selectedIds={new Set(selectedTargets.map(editTargetToNodeId))}
              ariaLabel={copy.selectTimelineItems}
            />
          </div>
        )}
      </FloatingMenu>
    </div>
  )
}
